import React from 'react';
import { motion } from 'framer-motion';
import { BookOpenIcon, ClipboardCheckIcon, BriefcaseIcon, WavesIcon, FileTextIcon, CheckIcon, PackageIcon } from 'lucide-react';
const items = [{
  Icon: BookOpenIcon,
  title: 'Los 6 Tomos del Recorrido Graduado',
  desc: 'Una guía completa con objetivos mensuales, evaluaciones y sesiones de alto impacto.',
  bullets: ['Ambientación e Iniciación 1 y 2', 'Perfeccionamiento y Entrenamiento', 'Programa para adultos máster'],
  format: 'PDF · 6 tomos',
  accent: 'from-blue-500 to-cyan-400',
  iconColor: 'text-blue-400',
  iconBg: 'bg-blue-500/10',
  iconBorder: 'border-blue-500/20',
  glow: 'shadow-blue-500/20',
  hoverBorder: 'group-hover:border-blue-500/50'
}, {
  Icon: ClipboardCheckIcon,
  title: 'Kit de Evaluación Express',
  desc: 'Planillas interactivas para ubicar el nivel del alumno en 5 minutos.',
  bullets: ['Diagnóstico en 5 minutos', 'Justifica avances ante los padres', 'Plantillas reutilizables'],
  format: 'PDF interactivo',
  accent: 'from-purple-500 to-fuchsia-400',
  iconColor: 'text-purple-400',
  iconBg: 'bg-purple-500/10',
  iconBorder: 'border-purple-500/20',
  glow: 'shadow-purple-500/20',
  hoverBorder: 'group-hover:border-purple-500/50'
}, {
  Icon: BriefcaseIcon,
  title: 'Natación Business Pro',
  desc: 'El manual táctico para gestionar grupos complejos y rentabilizar tus clases.',
  bullets: ['Manejo de grupos mixtos', 'Estrategia para subir tarifas', 'Aumenta retención de alumnos'],
  format: 'Manual PDF',
  accent: 'from-emerald-500 to-green-400',
  iconColor: 'text-emerald-400',
  iconBg: 'bg-emerald-500/10',
  iconBorder: 'border-emerald-500/20',
  glow: 'shadow-emerald-500/20',
  hoverBorder: 'group-hover:border-emerald-500/50'
}, {
  Icon: WavesIcon,
  title: 'Manual Técnico de Estilos',
  desc: 'Fichas de corrección técnica para los 4 estilos olímpicos.',
  bullets: ['Crol y Espalda', 'Pecho y Mariposa', 'Errores comunes y correcciones'],
  format: 'PDF · 4 estilos',
  accent: 'from-amber-500 to-orange-400',
  iconColor: 'text-amber-400',
  iconBg: 'bg-amber-500/10',
  iconBorder: 'border-amber-500/20',
  glow: 'shadow-amber-500/20',
  hoverBorder: 'group-hover:border-amber-500/50'
}];
export function ContentPreview() {
  return <section className="w-full bg-bg-dark py-24 px-4 relative overflow-hidden" data-id="element-70">
      {/* Decorative blobs */}
      <div aria-hidden className="absolute top-1/4 -left-20 w-[400px] h-[400px] bg-primary/5 rounded-full blur-3xl pointer-events-none" data-id="element-71" />
      <div aria-hidden className="absolute bottom-1/4 -right-20 w-[400px] h-[400px] bg-purple-500/5 rounded-full blur-3xl pointer-events-none" data-id="element-72" />

      <div className="max-w-6xl mx-auto relative z-10" data-id="element-73">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-50px'
      }} transition={{
        duration: 0.5
      }} className="text-center mb-16" data-id="element-74">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-primary/10 border border-primary/20 mb-6" data-id="element-75">
            <PackageIcon className="w-4 h-4 text-primary" data-id="element-76" />
            <span className="text-sm font-semibold text-primary tracking-wide uppercase" data-id="element-77">
              El contenido del pack
            </span>
          </div>

          <h2 className="text-4xl sm:text-5xl lg:text-6xl font-black text-text-main mb-5 tracking-tight" data-id="element-78">
            ¿Qué hay dentro del MegaPack?
          </h2>
          <p className="text-text-sec text-lg sm:text-xl max-w-2xl mx-auto" data-id="element-79">
            Cuatro herramientas digitales en PDF,{' '}
            <span className="text-text-main font-semibold" data-id="element-80">
              listas para usar hoy mismo
            </span>
            .
          </p>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 sm:gap-6" data-id="element-81">
          {items.map((item, i) => {
          const {
            Icon
          } = item;
          return <motion.div key={i} initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-50px'
          }} transition={{
            duration: 0.5,
            delay: i * 0.1
          }} whileHover={{
            y: -6
          }} className={`group relative bg-bg-alt rounded-3xl border border-border-light overflow-hidden flex flex-col transition-all duration-300 hover:shadow-2xl ${item.glow} ${item.hoverBorder}`} data-id="element-82">
                {/* Top accent gradient bar */}
                <div className={`h-1 w-full bg-gradient-to-r ${item.accent} opacity-70 group-hover:opacity-100 transition-opacity`} data-id="element-83" />

                {/* Subtle corner glow on hover */}
                <div className={`absolute -top-20 -right-20 w-48 h-48 rounded-full bg-gradient-to-br ${item.accent} opacity-0 group-hover:opacity-10 blur-3xl transition-opacity duration-500 pointer-events-none`} data-id="element-84" />

                <div className="flex-1 flex flex-col p-7 sm:p-8 relative z-10" data-id="element-85">
                  {/* Icon + format badge row */}
                  <div className="flex items-start justify-between gap-3 mb-5" data-id="element-86">
                    <div className={`w-14 h-14 rounded-2xl ${item.iconBg} border ${item.iconBorder} flex items-center justify-center ${item.iconColor} group-hover:scale-110 transition-transform duration-300`} data-id="element-87">
                      <Icon className="w-7 h-7" strokeWidth={2} data-id="element-88" />
                    </div>
                    <span className={`inline-flex items-center gap-1.5 mt-1 text-[11px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full ${item.iconBg} ${item.iconColor} border ${item.iconBorder} whitespace-nowrap`} data-id="element-89">
                      <FileTextIcon className="w-3 h-3" data-id="element-90" />
                      {item.format}
                    </span>
                  </div>

                  <h3 className="text-xl sm:text-2xl font-bold text-text-main leading-snug mb-3" data-id="element-91">
                    {item.title}
                  </h3>
                  <p className="text-text-sec text-base leading-relaxed mb-5" data-id="element-92">
                    {item.desc}
                  </p>

                  {/* Bullet list */}
                  <ul className="space-y-2 mt-auto pt-4 border-t border-border-light" data-id="element-93">
                    {item.bullets.map((bullet, j) => <li key={j} className="flex items-start gap-2.5" data-id="element-94">
                        <div className={`w-4 h-4 rounded-full ${item.iconBg} ${item.iconColor} border ${item.iconBorder} flex items-center justify-center flex-shrink-0 mt-0.5`} data-id="element-95">
                          <CheckIcon className="w-2.5 h-2.5" strokeWidth={3} data-id="element-96" />
                        </div>
                        <span className="text-text-sec text-sm leading-snug" data-id="element-97">
                          {bullet}
                        </span>
                      </li>)}
                  </ul>
                </div>
              </motion.div>;
        })}
        </div>

        {/* Footer reassurance */}
        <motion.p initial={{
        opacity: 0
      }} whileInView={{
        opacity: 1
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5,
        delay: 0.4
      }} className="text-center text-text-sec text-sm mt-10 flex items-center justify-center gap-2 flex-wrap" data-id="element-98">
          <span className="inline-flex items-center gap-1.5" data-id="element-99">
            <FileTextIcon className="w-4 h-4 text-primary" data-id="element-100" />
            100% digital
          </span>
          <span className="text-border-light" data-id="element-101">•</span>
          <span data-id="element-102">Imprimible</span>
          <span className="text-border-light" data-id="element-103">•</span>
          <span data-id="element-104">Acceso inmediato</span>
        </motion.p>
      </div>
    </section>;
}